/**
 * AdminLandingTab — CMS-style editor for the marketing landing page.
 * Every text/image field on the landing is editable here. Structure is
 * fixed; content is fetched from + saved to /api/seo/landing-content.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Reusable field primitives */
const TextField = ({ value, onChange, label, testId, placeholder }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder} className="bg-[#0a0a0a] border-gray-700 text-gray-100 placeholder:text-gray-500" />
  </div>
);

const TextArea = ({ value, onChange, label, testId, rows = 3, placeholder }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</Label>
    <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} rows={rows} placeholder={placeholder} className="bg-[#0a0a0a] border-gray-700 text-gray-100 placeholder:text-gray-500" />
  </div>
);

const ImageField = ({ value, onChange, label, testId, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</Label>
    <div className="flex items-start gap-4">
      {value && (
        <img src={value} alt={label} className="w-24 h-24 object-contain rounded border border-gray-700 bg-[#0a0a0a]" />
      )}
      <div className="flex-1">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder="/landing/... or full URL" className="bg-[#0a0a0a] border-gray-700 text-gray-100 placeholder:text-gray-500" />
        {hint && <p className="mt-1 text-xs text-gray-500 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {hint}</p>}
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, subtitle, children }) => (
  <Card className="bg-[#0a0a0a] border-gray-800">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg text-gray-200">{title}</CardTitle>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

export function AdminLandingTab() {
  const [content, setContent] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/seo/landing-content`)
      .then((r) => r.json())
      .then((d) => { setContent(d); setInitial(JSON.parse(JSON.stringify(d))); })
      .catch(() => toast.error('Failed to load landing content'));
  }, []);

  const updateSection = (section, key, val) => {
    setContent((c) => ({ ...c, [section]: { ...(c[section] || {}), [key]: val } }));
  };

  const updateArrayItem = (section, arrKey, idx, key, val) => {
    setContent((c) => {
      const items = [...((c[section] || {})[arrKey] || [])];
      items[idx] = { ...items[idx], [key]: val };
      return { ...c, [section]: { ...(c[section] || {}), [arrKey]: items } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/seo/landing-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!r.ok) throw new Error('save failed');
      setInitial(JSON.parse(JSON.stringify(content)));
      toast.success('Landing content saved');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const resetDefaults = async () => {
    if (!confirm('Reset landing content to hardcoded defaults? This will overwrite all your edits.')) return;
    setResetting(true);
    try {
      await fetch(`${API_URL}/api/seo/landing-content/reset`, { method: 'POST' });
      const r = await fetch(`${API_URL}/api/seo/landing-content`);
      const d = await r.json();
      setContent(d);
      setInitial(JSON.parse(JSON.stringify(d)));
      toast.success('Reset to defaults');
    } catch {
      toast.error('Failed to reset');
    }
    setResetting(false);
  };

  if (!content) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading landing content…</div>;
  }

  const dirty = JSON.stringify(content) !== JSON.stringify(initial);
  const c = content;

  return (
    <div className="space-y-6" data-testid="admin-landing-tab">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur border-b border-gray-800 -mx-6 px-6 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-200">Landing Page Content</h2>
          <p className="text-xs text-gray-500 mt-0.5">Edit copy and images across all 14 sections. Header/Footer/FAQ are managed separately.</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs font-semibold text-orange-400">Unsaved changes</span>}
          <Button variant="outline" onClick={resetDefaults} disabled={resetting} data-testid="landing-reset-btn" className="border-gray-700 bg-transparent text-gray-300 hover:bg-gray-900 hover:text-gray-100">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Reset to defaults
          </Button>
          <Button onClick={save} disabled={saving || !dirty} data-testid="landing-save-btn"
                  style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* SEO */}
      <SectionCard title="SEO metadata" subtitle="Title tag, meta description, and Open Graph fields for the homepage.">
        <TextField label="Page title (≤60 chars)" value={c.seo?.title}
                   onChange={(v) => updateSection('seo', 'title', v)} testId="seo-title" />
        <TextArea label="Meta description (≤160 chars)" value={c.seo?.description} rows={2}
                  onChange={(v) => updateSection('seo', 'description', v)} testId="seo-description" />
        <TextArea label="Keywords (comma-separated)" value={c.seo?.keywords} rows={2}
                  onChange={(v) => updateSection('seo', 'keywords', v)} testId="seo-keywords" />
        <TextField label="OG title" value={c.seo?.og_title}
                   onChange={(v) => updateSection('seo', 'og_title', v)} testId="seo-og-title" />
        <TextArea label="OG description" value={c.seo?.og_description} rows={2}
                  onChange={(v) => updateSection('seo', 'og_description', v)} testId="seo-og-desc" />
        <ImageField label="OG image (1200×630)" value={c.seo?.og_image}
                    onChange={(v) => updateSection('seo', 'og_image', v)} testId="seo-og-image"
                    hint="Recommended size 1200×630 px. Used for social share previews." />
      </SectionCard>

      {/* 1. HERO */}
      <SectionCard title="1. Hero section" subtitle="Top of the page — badge, headline, sub, body, CTAs, footnote, hero image.">
        <TextField label="Badge (small pill)" value={c.hero?.badge} onChange={(v) => updateSection('hero','badge',v)} testId="hero-badge" />
        <TextField label="Headline (H1)" value={c.hero?.headline} onChange={(v) => updateSection('hero','headline',v)} testId="hero-headline" />
        <TextField label="Sub-headline" value={c.hero?.sub_headline} onChange={(v) => updateSection('hero','sub_headline',v)} testId="hero-sub" />
        <TextArea label="Body copy" value={c.hero?.body} onChange={(v) => updateSection('hero','body',v)} testId="hero-body" />
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Primary CTA label" value={c.hero?.cta_primary_label} onChange={(v) => updateSection('hero','cta_primary_label',v)} testId="hero-cta1-label" />
          <TextField label="Primary CTA note" value={c.hero?.cta_primary_note} onChange={(v) => updateSection('hero','cta_primary_note',v)} testId="hero-cta1-note" />
          <TextField label="Secondary CTA label" value={c.hero?.cta_secondary_label} onChange={(v) => updateSection('hero','cta_secondary_label',v)} testId="hero-cta2-label" />
          <TextField label="Secondary CTA note" value={c.hero?.cta_secondary_note} onChange={(v) => updateSection('hero','cta_secondary_note',v)} testId="hero-cta2-note" />
        </div>
        <TextArea label="Footnote (italic, small)" value={c.hero?.footnote} onChange={(v) => updateSection('hero','footnote',v)} testId="hero-footnote" />
        <ImageField label="Hero image URL" value={c.hero?.image_url} onChange={(v) => updateSection('hero','image_url',v)} testId="hero-image" hint="Recommended: transparent PNG on the right side." />
        <TextField label="Hero image alt text" value={c.hero?.image_alt} onChange={(v) => updateSection('hero','image_alt',v)} testId="hero-image-alt" />
      </SectionCard>

      {/* 2. BREAKER */}
      <SectionCard title="2. Breaker banner" subtitle="Slim gradient band.">
        <TextField label="Small headline (uppercase strap)" value={c.breaker?.headline} onChange={(v) => updateSection('breaker','headline',v)} testId="breaker-headline" />
        <TextField label="Sub-headline (large)" value={c.breaker?.sub_headline} onChange={(v) => updateSection('breaker','sub_headline',v)} testId="breaker-sub" />
      </SectionCard>

      {/* 3. PAIN POINTS */}
      <SectionCard title="3. Pain points" subtitle="Section header + four quote-mark cards.">
        <TextField label="Section header (H2)" value={c.pain_points?.section_header}
                   onChange={(v) => updateSection('pain_points','section_header',v)} testId="pain-header" />
        {(c.pain_points?.items || []).map((it, idx) => (
          <TextField key={idx} label={`Pain point ${idx + 1}`} value={it.quote}
                     onChange={(v) => updateArrayItem('pain_points','items',idx,'quote',v)} testId={`pain-${idx}`} />
        ))}
      </SectionCard>

      {/* 4. PROBLEM ID */}
      <SectionCard title="4. Problem identification" subtitle="70/30 split with bullets.">
        <TextField label="Headline (line 1)" value={c.problem_identification?.headline_left} onChange={(v) => updateSection('problem_identification','headline_left',v)} testId="pi-h1" />
        <TextField label="Headline (line 2, gradient)" value={c.problem_identification?.headline_left_2} onChange={(v) => updateSection('problem_identification','headline_left_2',v)} testId="pi-h2" />
        <TextArea label="Sub-headline" value={c.problem_identification?.sub_headline_left} onChange={(v) => updateSection('problem_identification','sub_headline_left',v)} testId="pi-sub" />
        {(c.problem_identification?.bullets || []).map((b, idx) => (
          <TextField key={idx} label={`Bullet ${idx + 1}`} value={b}
                     onChange={(v) => setContent((c2) => {
                       const bullets = [...(c2.problem_identification?.bullets || [])]; bullets[idx] = v;
                       return { ...c2, problem_identification: { ...(c2.problem_identification || {}), bullets } };
                     })} testId={`pi-bullet-${idx}`} />
        ))}
        <TextField label="Bottom line (teal accent)" value={c.problem_identification?.bottom_line} onChange={(v) => updateSection('problem_identification','bottom_line',v)} testId="pi-bottom" />
      </SectionCard>

      {/* 5. PROOF 1 */}
      <SectionCard title="5. Proof 1 — 12 archetype flip cards" subtitle="Each card flips to show a brand-logo example. Edit logo URL per archetype below.">
        <TextField label="Strap" value={c.proof_1?.strap} onChange={(v) => updateSection('proof_1','strap',v)} testId="p1-strap" />
        <TextField label="Headline" value={c.proof_1?.headline} onChange={(v) => updateSection('proof_1','headline',v)} testId="p1-headline" />
        <TextArea label="Body" value={c.proof_1?.body} onChange={(v) => updateSection('proof_1','body',v)} testId="p1-body" />
        <div className="pt-2 space-y-2">
          {(c.proof_1?.archetypes || []).map((a, idx) => (
            <div key={a.name} className="flex items-center gap-3 p-2 border border-gray-800 rounded-lg bg-[#111]">
              <div className="w-14 flex-shrink-0 text-xs font-bold uppercase text-gray-300">{a.name}</div>
              <img src={a.logo} alt="" className="w-10 h-10 object-contain bg-white rounded border border-gray-700" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
              <div className="flex-1">
                <Input
                  value={a.logo || ''}
                  onChange={(e) => updateArrayItem('proof_1', 'archetypes', idx, 'logo', e.target.value)}
                  placeholder="/landing/logos/name.png"
                  data-testid={`p1-logo-${a.name.toLowerCase()}`}
                  className="bg-[#0a0a0a] border-gray-700 text-gray-100 placeholder:text-gray-500"
                />
              </div>
            </div>
          ))}
        </div>
        <TextArea label="Disclaimer (small, bottom of section)" value={c.proof_1?.disclaimer}
                  onChange={(v) => updateSection('proof_1','disclaimer',v)} rows={3} testId="p1-disclaimer" />
      </SectionCard>

      {/* 6. PROOF 2 */}
      <SectionCard title="6. Proof 2 — Jung">
        <TextField label="Strap" value={c.proof_2?.strap} onChange={(v) => updateSection('proof_2','strap',v)} testId="p2-strap" />
        <TextField label="Headline" value={c.proof_2?.headline} onChange={(v) => updateSection('proof_2','headline',v)} testId="p2-headline" />
        <TextArea label="Body" value={c.proof_2?.body} onChange={(v) => updateSection('proof_2','body',v)} rows={4} testId="p2-body" />
        <ImageField label="Jung image URL" value={c.proof_2?.image_url} onChange={(v) => updateSection('proof_2','image_url',v)} testId="p2-image" />
        <TextField label="Image alt text" value={c.proof_2?.image_alt} onChange={(v) => updateSection('proof_2','image_alt',v)} testId="p2-alt" />
        <TextField label="Image credit line" value={c.proof_2?.image_credit} onChange={(v) => updateSection('proof_2','image_credit',v)} testId="p2-credit" />
      </SectionCard>

      {/* 7. CTA MID 1 */}
      <SectionCard title="7. CTA (mid 1)">
        <TextField label="Strap" value={c.cta_mid_1?.strap} onChange={(v) => updateSection('cta_mid_1','strap',v)} testId="cta1-strap" />
        <TextField label="Headline" value={c.cta_mid_1?.headline} onChange={(v) => updateSection('cta_mid_1','headline',v)} testId="cta1-headline" />
        <TextField label="Sub-headline" value={c.cta_mid_1?.sub_headline} onChange={(v) => updateSection('cta_mid_1','sub_headline',v)} testId="cta1-sub" />
        <TextField label="Button label" value={c.cta_mid_1?.cta_label} onChange={(v) => updateSection('cta_mid_1','cta_label',v)} testId="cta1-label" />
      </SectionCard>

      {/* 8. SNEAK PEEK */}
      <SectionCard title="8. Sneak peek">
        <TextField label="Strap" value={c.sneak_peek?.strap} onChange={(v) => updateSection('sneak_peek','strap',v)} testId="sp-strap" />
        <TextArea label="Headline" value={c.sneak_peek?.headline} onChange={(v) => updateSection('sneak_peek','headline',v)} rows={2} testId="sp-headline" />
        <ImageField label="Report image URL" value={c.sneak_peek?.image_url} onChange={(v) => updateSection('sneak_peek','image_url',v)} testId="sp-image" />
        <TextField label="Image alt text" value={c.sneak_peek?.image_alt} onChange={(v) => updateSection('sneak_peek','image_alt',v)} testId="sp-alt" />
        <div className="space-y-3 pt-2">
          {(c.sneak_peek?.items || []).map((it, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">
                  <Label className="text-xs">Tier</Label>
                  <select value={it.tier} onChange={(e) => updateArrayItem('sneak_peek','items',idx,'tier',e.target.value)}
                          className="mt-1 block w-full h-9 rounded-md border border-gray-300 text-sm px-2">
                    <option value="FREE">FREE</option>
                    <option value="PREMIUM">PREMIUM</option>
                  </select>
                </div>
                <div className="col-span-3">
                  <TextField label={`Item ${idx + 1} title`} value={it.title} onChange={(v) => updateArrayItem('sneak_peek','items',idx,'title',v)} testId={`sp-item-${idx}-title`} />
                </div>
              </div>
              <TextArea label="Item description" value={it.body} onChange={(v) => updateArrayItem('sneak_peek','items',idx,'body',v)} testId={`sp-item-${idx}-body`} />
              <TextField label="Highlighted phrase (cyan, appended after body)" value={it.highlight} onChange={(v) => updateArrayItem('sneak_peek','items',idx,'highlight',v)} testId={`sp-item-${idx}-highlight`} />
            </div>
          ))}
        </div>
        <TextField label="CTA label" value={c.sneak_peek?.cta_label} onChange={(v) => updateSection('sneak_peek','cta_label',v)} testId="sp-cta" />
        <TextField label="CTA note" value={c.sneak_peek?.cta_note} onChange={(v) => updateSection('sneak_peek','cta_note',v)} testId="sp-cta-note" />
      </SectionCard>

      {/* 9. PROMISE */}
      <SectionCard title="9. Promise — transformation">
        <TextField label="Headline" value={c.promise?.headline} onChange={(v) => updateSection('promise','headline',v)} testId="pr-headline" />
        <TextField label="Sub-headline (gradient)" value={c.promise?.sub_headline} onChange={(v) => updateSection('promise','sub_headline',v)} testId="pr-sub" />
        <ImageField label="Desktop image URL" value={c.promise?.image_web_url} onChange={(v) => updateSection('promise','image_web_url',v)} testId="pr-img-web" hint="Shown on ≥768 px screens." />
        <ImageField label="Mobile image URL" value={c.promise?.image_mobile_url} onChange={(v) => updateSection('promise','image_mobile_url',v)} testId="pr-img-mobile" hint="Shown on <768 px screens." />
        <TextField label="Image alt text (both)" value={c.promise?.image_alt} onChange={(v) => updateSection('promise','image_alt',v)} testId="pr-alt" />
      </SectionCard>

      {/* 10. CTA MID 2 */}
      <SectionCard title="10. CTA (mid 2) — after Promise">
        <TextField label="Tagline (uppercase, gradient, above button)" value={c.cta_mid_2?.tagline} onChange={(v) => updateSection('cta_mid_2','tagline',v)} testId="cta2-tagline" />
        <TextField label="Button label" value={c.cta_mid_2?.cta_label} onChange={(v) => updateSection('cta_mid_2','cta_label',v)} testId="cta2-label" />
        <TextField label="Button note" value={c.cta_mid_2?.cta_note} onChange={(v) => updateSection('cta_mid_2','cta_note',v)} testId="cta2-note" />
      </SectionCard>

      {/* 13. FINAL CTA */}
      <SectionCard title="13. Final CTA">
        <TextField label="Strap" value={c.cta_final?.strap} onChange={(v) => updateSection('cta_final','strap',v)} testId="ctaf-strap" />
        <TextField label="Headline" value={c.cta_final?.headline} onChange={(v) => updateSection('cta_final','headline',v)} testId="ctaf-headline" />
        <TextArea label="Sub-headline" value={c.cta_final?.sub_headline} onChange={(v) => updateSection('cta_final','sub_headline',v)} rows={3} testId="ctaf-sub" />
        <TextField label="Button label" value={c.cta_final?.cta_label} onChange={(v) => updateSection('cta_final','cta_label',v)} testId="ctaf-label" />
        <TextField label="Button note" value={c.cta_final?.cta_note} onChange={(v) => updateSection('cta_final','cta_note',v)} testId="ctaf-note" />
      </SectionCard>

      {/* RESULTS PAGE PAYWALL */}
      <SectionCard title="Results Page — Paywall & Pricing" subtitle="Controls the hard-sell + pricing shown after the quiz to non-paying users.">
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Anchor price (₹, strike-through)" value={c.results_page?.price_anchor}
                     onChange={(v) => updateSection('results_page','price_anchor', parseInt(v) || 0)} testId="rp-anchor" />
          <TextField label="Current price (₹, offered)" value={c.results_page?.price_current}
                     onChange={(v) => updateSection('results_page','price_current', parseInt(v) || 0)} testId="rp-current" />
          <TextField label="Urgency countdown (hours)" value={c.results_page?.urgency_hours}
                     onChange={(v) => updateSection('results_page','urgency_hours', parseInt(v) || 0)} testId="rp-hours" />
          <TextField label="CTA button label" value={c.results_page?.cta_label}
                     onChange={(v) => updateSection('results_page','cta_label',v)} testId="rp-cta" />
        </div>
        <TextField label="Urgency message" value={c.results_page?.urgency_message}
                   onChange={(v) => updateSection('results_page','urgency_message',v)} testId="rp-urgency-msg" />
        <TextField label="Locked Secondary — headline" value={c.results_page?.locked_secondary_headline}
                   onChange={(v) => updateSection('results_page','locked_secondary_headline',v)} testId="rp-locked-h" />
        <TextArea label="Locked Secondary — sub-headline" value={c.results_page?.locked_secondary_sub}
                  onChange={(v) => updateSection('results_page','locked_secondary_sub',v)} rows={2} testId="rp-locked-sub" />
        <TextField label="Hard-sell strap" value={c.results_page?.hard_sell_strap}
                   onChange={(v) => updateSection('results_page','hard_sell_strap',v)} testId="rp-strap" />
        <TextField label="Hard-sell headline" value={c.results_page?.hard_sell_headline}
                   onChange={(v) => updateSection('results_page','hard_sell_headline',v)} testId="rp-hs-h" />
        <TextArea label="Hard-sell sub" value={c.results_page?.hard_sell_sub}
                  onChange={(v) => updateSection('results_page','hard_sell_sub',v)} rows={2} testId="rp-hs-sub" />
        <div className="space-y-2 pt-2">
          <Label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Value stack bullets (8)</Label>
          {(c.results_page?.value_bullets || []).map((b, idx) => (
            <TextField key={idx} label={`Bullet ${idx + 1}`} value={b}
                       onChange={(v) => setContent((cc) => {
                         const arr = [...(cc.results_page?.value_bullets || [])]; arr[idx] = v;
                         return { ...cc, results_page: { ...(cc.results_page || {}), value_bullets: arr } };
                       })} testId={`rp-bullet-${idx}`} />
          ))}
        </div>
        <TextField label="Why buy now — headline" value={c.results_page?.why_buy_now_headline}
                   onChange={(v) => updateSection('results_page','why_buy_now_headline',v)} testId="rp-why-h" />
        <TextArea label="Money-back note" value={c.results_page?.money_back_note}
                  onChange={(v) => updateSection('results_page','money_back_note',v)} rows={2} testId="rp-refund" />
      </SectionCard>

      {/* Bottom save bar duplicate */}
      <div className="flex justify-end pt-4">
        <Button onClick={save} disabled={saving || !dirty}
                style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save all changes
        </Button>
      </div>
    </div>
  );
}

export default AdminLandingTab;
